import { BreakpointObserver } from '@angular/cdk/layout';
import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { IVerificationInterface, emptyVerificationInterface } from 'src/app/shared/models/verification.interface';
import { ClaimService } from 'src/app/services/claim.service';
import { UserService } from 'src/app/services/user.service';
import { DataService } from 'src/app/services/data.service';
import { filter, take } from 'rxjs/operators';
import { DynamicFlowService } from 'src/app/services/dynamic-flow.service';
import { GuestLinkPreview } from 'src/app/shared/models/guest-link-preview';
import { GuestLinkService } from 'src/app/services/guest-link.service';
import { CssThemesService } from 'src/app/services/css-themes.service';
import { environment } from 'src/environments/environment';
import { LoginService } from 'src/app/services/login.service';
import { SspService } from 'src/app/services/ssp.service';
import { emptyClaim } from 'src/app/shared/models/claim.interface';
import { ScriptService } from 'src/app/services/script.service';

@Component({
  selector: 'upsc-customized-landing',
  templateUrl: './customized-landing.component.html',
  styleUrls: ['./customized-landing.component.scss']
})
export class CustomizedLandingComponent {

  recaptchaSiteKey = environment.recaptcha.siteKeyV3;
  recaptchaSecret = environment.recaptcha.secretV3;
  customerForm: UntypedFormGroup;
  systemError: boolean = false;
  verifyCodeError: boolean = false;
  policyMatchError: boolean = false;
  shipmentInformationError: boolean = false;
  currentStep = 0;
  loading: boolean = false;
  loading2: boolean = false;
  liabilityError: boolean = false;
  duplicateTrackingNumber: boolean = false;
  isInsuredValueZero: boolean = false;
  public createVerificationInterface: IVerificationInterface = emptyVerificationInterface;
  verifyCodeInterface = { emailId: "", toVerify: "", workflow: "" };
  channelPartner = '';
  channelPartnerDisplayName = '';
  previewModel: GuestLinkPreview = {};
  flowType = '';
  upsHealthCareDeclaredAmountError: boolean = false;
  trackingNumberNotFound: boolean = false;
  invalidTrackingNumber: boolean = false;
  shipDateError: boolean = false;
  appStateTarget = '';
  email = '';
  isPirateShipEncryptedFlow = false;
  emailAddress: any;
  showSpinner: boolean;
  noPackageEligible: boolean;
  confirmationNumber = '';
  coverageFound: boolean;
  directUrl: boolean;
  isLoading = true;
  psEncryptedLandingSpinner = false;
  mustCompleteRecaptcha: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private titleService: Title,
    private fb: UntypedFormBuilder,
    private loginService: LoginService,
    private breakpointObserver: BreakpointObserver,
    private claimService: ClaimService,
    private router: Router,
    private userService: UserService,
    private dataService: DataService,
    private dynamicFlowService: DynamicFlowService,
    private sspService: SspService,
    private guestLinkService: GuestLinkService,
    private cssThemesService: CssThemesService,
    private auth: AuthService,
    private scriptService:ScriptService
  ) {

    const encryptedParam = this.route.snapshot.queryParamMap.get('eqp');
    if (this.router.url.startsWith('/pirate-ship') && encryptedParam) {
      this.isPirateShipEncryptedFlow = true;
      sessionStorage.setItem('pirateShipLanding', this.router.url);
      sessionStorage.setItem('pirateShipEncryptedParam', encryptedParam.replace(/ /g, '+'));
      this.flowType = 'pirateship';
      this.titleService.setTitle("Pirate Ship | Claims Portal");
      this.psEncryptedLandingSpinner = true;
    }
    else if (this.router.url.startsWith('/pirate-ship')  && sessionStorage.getItem('pirateShipEncryptedParam')) {
      this.flowType = 'pirateship';
      this.titleService.setTitle("Pirate Ship | Claims Portal");
      this.psEncryptedLandingSpinner = false;
    }

    //get channel partner and channel partner display name
    this.channelPartner = this.dynamicFlowService.setChannelPartner();
    this.channelPartnerDisplayName = this.dynamicFlowService.setChannelPartnerName();
    this.flowType = sessionStorage.getItem("flowType") || (this.isPirateShipEncryptedFlow ? 'pirateship' : '');

    this.customerForm = this.fb.group({
      trackingNumber: ['', Validators.compose([Validators.required])],
      recaptchaControl: [null],
      terms: [false]
    });

    if (this.flowType !== 'pirateship') {
      this.customerForm.get('terms').setValidators(Validators.requiredTrue);
    }

    if (this.isPirateShipEncryptedFlow) {
      this.determineLoginState();
    }
    this.downloadCaptcha();
  }

  ngOnInit(): void {
    if (this.isPirateShipEncryptedFlow) {
      return;
    }

    this.gatherAppState(); //Auth0
    this.determineLoginState();
    this.currentStep = 0;
    this.loading = false;
    this.loading2 = false;
    this.systemError = false;
    this.verifyCodeError = false;

    // Clear the navigation flag when returning to landing page
    sessionStorage.removeItem('pirateShipNavigateToClaims');

    // Reset CSS theme variables to defaults to prevent styles from other portals bleeding through
    this.guestLinkService.setThemeDefaultStyles();

    // Reload pirateship CSS to ensure correct theme is applied
    if (this.flowType === 'pirateship') {
      this.cssThemesService.loadCustomizedClaimsStyle('pirateship');
    }

    this.route.queryParams.subscribe(params => {
      if (this.router.url.startsWith('/pirate-ship') && params.eqp) {
        sessionStorage.setItem('pirateShipLanding', this.router.url);
        this.flowType = 'pirateship';
        sessionStorage.setItem('pirateShipEncryptedParam', params.eqp.replace(/ /g, '+'));
        this.titleService.setTitle("Pirate Ship | Claims Portal");
        this.isLoading = true;
      } else if (this.router.url.startsWith('/pirate-ship')) {
        let paramInfo = JSON.parse(sessionStorage.getItem('paramInfo'));
        if (paramInfo && paramInfo.trackingNumber && paramInfo.email) {
          const trimTrackingNumber = paramInfo.trackingNumber.trim();
          this.customerForm.controls.trackingNumber.setValue(trimTrackingNumber);
          this.customerForm.controls.email.setValue(paramInfo.email);
          this.customerForm.patchValue({ //updating mat form fields
            trackingNumber: trimTrackingNumber,
            email: paramInfo.email
          });
          this.isLoading = false;
        } else {
          this.flowType = 'pirateship';
          this.titleService.setTitle("Pirate Ship | Claims Portal");
          // Don't set directUrl for the encrypted flow - TN is filled programmatically and should stay readonly
          if (!sessionStorage.getItem('pirateShipEncryptedParam')) {
            this.directUrl = true;
          }
          this.isLoading = false;
        }
      } else {
        this.flowType = 'default';
        this.customerForm.addControl('terms', this.fb.control(false, Validators.requiredTrue));
        this.titleService.setTitle("UPS Capital Customized Claims Portal");
        this.isLoading = false;
      }
    });

    sessionStorage.removeItem('documentUploadParams');
    sessionStorage.removeItem('documentUploadedFiles');
  }
  
//Will call recaptcha API and return a score for how likely a user is a bot.
  //closer to 0 is more likely a bot. closer to 1 is more likely a human
  reCAPTCHA(e?) {
    this.loading = true;
    //If user must complete a manual recaptcha, skip the API call.
    if (this.mustCompleteRecaptcha) {
      this.getStarted();
      return;
    }
    grecaptcha.ready(() => {
      grecaptcha.execute(this.recaptchaSiteKey, { action: 'submit' }).then((token) => {
        let request = {
          secret: this.recaptchaSecret,
          response: token,
          remoteip: ''
        }
        this.loginService.recaptchaCheck(request).subscribe(
          data => {
            if (data && data.score <= 0.2) {
              //show manual recaptcha
              this.loading = false;
              this.showReCAPTCHA();
            } else {
              //let user proceed
              this.getStarted();
            }
          }, error => {
            this.showReCAPTCHA();
          }
        );
      });
    });
  }
  downloadCaptcha() {
    this.scriptService.downloadCaptcha();
  }
  showReCAPTCHA() {
    this.mustCompleteRecaptcha = true;
    this.customerForm.controls.recaptchaControl.setValidators(Validators.required);
  }

  decryptUserDetails(eqp: string): void {
    let eqpToken = eqp.replace(/ /g, '+'); //start encoding
    localStorage.setItem('eqp', eqpToken);
    this.userService.decryptParams({ token: eqpToken }).subscribe( //decrypting the token
      data => { //trackingNumber and email from decryption
        if (data.trackingNumber && data.email) {
          sessionStorage.setItem('paramInfo', JSON.stringify(data)); //network under paraminfo
          sessionStorage.setItem('emailAddress', data.email);
          this.emailAddress = data.email;
          this.customerForm.controls.trackingNumber.setValue(data.trackingNumber);
          //this.customerForm.controls.email.setValue(data.email);
          this.customerForm.patchValue({ //updating mat form fields
            trackingNumber: data.trackingNumber,
            email: data.email
          });
          this.isLoading = false;
        }
      },
      error => {
        this.showSpinner = false;
        this.systemError = true;
        this.isLoading = false;
      }
    );
  }

  getStarted() {
    if (this.customerForm.valid) {
      this.loading = true;
      this.isInsuredValueZero = false;
      this.systemError = false;
      this.liabilityError = false;
      this.duplicateTrackingNumber = false;
      this.dataService.deleteChannelPartnerShipmentDetails();
      this.dataService.deleteTrack3ShipmentDetails();
      this.policyMatchError = false;
      this.shipmentInformationError = false;
      this.shipDateError = false;
      this.systemError = false;
      this.trackingNumberNotFound = false;
      if (this.directUrl) {
        sessionStorage.setItem('emailAddress', this.email);
      }
      const trimmedTrackingNumber = this.customerForm.controls.trackingNumber.value.trim();
      this.claimService.getShipmentDetails(trimmedTrackingNumber).subscribe(
        data => {
          if (data?.trackingNumberDetails?.isDuplicateTrackingNumber == true) {
            this.loading = false;
            this.duplicateTrackingNumber = true;
          } else {
            if (this.channelPartner == 'inxpress') { // additional business logic needed for inxpress users when we call getShipmentDetails
              this.claimService.validateCCPTrackingNumber(trimmedTrackingNumber, this.channelPartner).subscribe(
                response => {
                  if (response?.data?.liability == false) { // if liability is false, do not allow them to continue
                    this.loading = false;
                    this.liabilityError = true;
                  } else if (response?.message?.toUpperCase() != 'SUCCEEDED') { // if getShipmentDetails fails, for inxpress users, do not allow them to continue
                    this.loading = false;
                    this.systemError = true;
                  } else if (response?.message?.toUpperCase() == 'NOT COVERED') {
                    this.loading = false;
                    this.isInsuredValueZero = true;
                  } else if (response?.message?.toUpperCase() === 'TRACKING NUMBER NOT FOUND') {
                    this.loading = false;
                    this.trackingNumberNotFound = true;
                  } else if (response?.message?.toUpperCase() === 'SUCCEEDED') {
                    this.trackingNumberNotFound = false;
                    this.systemError = false;
                    //this.saveVerificationCode();
                    this.checkVerificationCode();
                  } else {
                    this.loading = false;
                    this.systemError = true;
                    this.trackingNumberNotFound = false;
                  }
                }, error => {
                  if (error?.message?.toLowerCase() === 'timeout occurred') {
                    this.loading = false;
                    this.systemError = true;
                  } else {
                    this.loading = false;
                    this.systemError = true;
                  }
                }
              );
            } else if (this.flowType == 'pirateship') {
              this.claimService.validateCCPTrackingNumber(trimmedTrackingNumber, this.channelPartner).subscribe(
                response => {
                  if (response?.message?.toUpperCase() === 'SUCCEEDED') {
                    this.trackingNumberNotFound = false;
                    this.systemError = false;
                    //this.saveVerificationCode();
                    this.checkVerificationCode();
                  } else if (response?.message?.toUpperCase() === 'TRACKING NUMBER NOT FOUND') {
                    this.loading = false;
                    this.trackingNumberNotFound = true;
                  } else {
                    this.loading = false;
                    this.systemError = true;
                    this.trackingNumberNotFound = false;
                  }
                }, error => {
                  if (error?.message?.toLowerCase() === 'timeout occurred') {
                    this.loading = false;
                    this.systemError = true;
                  } else {
                    this.loading = false;
                    this.systemError = true;
                  }
                }
              );
            } else { // for non-inxpress users
              // Gathering here because I want to make sure CP data loads first before everything else. In Ideal scenario it loads after user gets an access token
              this.claimService.validateCCPTrackingNumber(trimmedTrackingNumber, this.channelPartner).subscribe(
                response => {
                  if (response?.message?.toUpperCase() === 'SUCCEEDED') {
                    this.trackingNumberNotFound = false;
                    this.systemError = false;
                    //this.saveVerificationCode();
                    this.checkVerificationCode();

                  } else if (response?.message?.toUpperCase() === 'TRACKING NUMBER NOT FOUND') {
                    this.loading = false;
                    this.trackingNumberNotFound = true;
                  } else {
                    this.loading = false;
                    this.systemError = true;
                    this.trackingNumberNotFound = false;
                  }
                }, error => {
                  if (error?.message?.toLowerCase() === 'timeout occurred') {
                    this.loading = false;
                    this.systemError = true;
                  } else {
                    this.loading = false;
                    this.systemError = true;
                  }
                }
              );
            }
          }
        }, error => {
          this.loading = false;
          this.systemError = true;
        }
      );
    }
  }

  initiatePasswordlessAuth() {
    if (this.flowType == 'pirateship') {
      // For encrypted Pirate Ship flow, decrypt first to get email and tracking number
      const encryptedParam = sessionStorage.getItem('pirateShipEncryptedParam');
      if (encryptedParam) {
        // Call decrypt API without access token to get email and tracking number
        this.userService.decryptPirateShipTrackingNumber({ token: encryptedParam }).subscribe(
          data => {
            if (data?.email && data?.trackingNumber) {
              // Store decrypted data in session storage
              sessionStorage.setItem('decryptedEmail', data.email);
              sessionStorage.setItem('decryptedTrackingNumber', data.trackingNumber);

              // Redirect to Auth0 with email as login_hint so it's prefilled and can't be changed
              this.auth.loginWithRedirect({
                authorizationParams: {
                  connection: 'email',
                  redirect_uri: `${environment.baseUrl.web}callback`,
                  'ext-connect': 'email',  //additional param to indicate template to hide "sign up" and "login" text
                  'ext-style': 'pirate-ship',
                  'ext-type': 'hint',
                  login_hint: data.email  // Pre-fill email on Auth0, user cannot change it
                },
                appState: { target: `/${this.channelPartner}` }
              });
            } else {
              this.psEncryptedLandingSpinner = false;
              this.systemError = true;
            }
          }, error => {
            this.psEncryptedLandingSpinner = false;
            this.systemError = true;
          }
        );
      } else {
        // Non-encrypted flow - proceed with normal Auth0 redirect
        this.auth.loginWithRedirect({
          authorizationParams: {
            connection: 'email',
            redirect_uri: `${environment.baseUrl.web}callback`,
            'ext-connect': 'email',
            'ext-style': 'pirate-ship'
          },
          appState: { target: `/${this.channelPartner}` }
        });
      }
    } else {
      this.auth.loginWithRedirect({
        authorizationParams: {
          connection: 'email',
          redirect_uri: `${environment.baseUrl.web}callback`,
          'ext-connect': 'email'  //additional param to indicate template to hide "sign up" and "login" text
          //redirect_uri: `${'http://localhost:4200/'}callback`
        },
        appState: { target: `/${this.channelPartner}` }
      });
    }
  }

  logout() {
    this.auth.logout();
    //Should also clear any storage of accessTokens
  }

  // Called after passwordless API succeeds for pirate-ship encrypted flow.
  // For encrypted flow: uses pre-decrypted data stored in session before Auth0 redirect
  // For non-encrypted flow: decrypts the encrypted param to retrieve the tracking number
  decryptPirateShipTrackingNumber() {
    // First check if we already have pre-decrypted data from before Auth0 redirect
    const decryptedEmail = sessionStorage.getItem('decryptedEmail');
    const decryptedTrackingNumber = sessionStorage.getItem('decryptedTrackingNumber');

    if (decryptedEmail && decryptedTrackingNumber) {
      // Use pre-decrypted data (from encrypted flow where decrypt happened before Auth0)
      this.customerForm.controls.trackingNumber.setValue(decryptedTrackingNumber);
      const tokenData = JSON.parse(sessionStorage.getItem('data') || '{}');
      // this.gatherChannelPartnerShipmentDetails(decryptedTrackingNumber, this.channelPartner, tokenData);
      return;
    }

    // Fallback: For non-encrypted flow, decrypt the tracking number using the encrypted param
    const encryptedParam = sessionStorage.getItem('pirateShipEncryptedParam');
    if (!encryptedParam) {
      return;
    }
    this.userService.decryptPirateShipTrackingNumber({ token: encryptedParam }).subscribe(
      data => {
        if (data?.trackingNumber) {
          this.customerForm.controls.trackingNumber.setValue(data.trackingNumber);
          const tokenData = JSON.parse(sessionStorage.getItem('data') || '{}');
          // this.router.navigateByUrl(`/pirate-ship`);
          this.isPirateShipEncryptedFlow = false;
          this.isLoading = false;
          // this.gatherChannelPartnerShipmentDetails(data.trackingNumber, this.channelPartner, tokenData);
        } else {
          this.loading = false;
          this.systemError = true;
        }
      },
      error => {
        this.loading = false;
        this.systemError = true;
      }
    );
  }

  //Gathers the app state from Auth0, which may have been set by another component when they called loginWithRedirect. We use this app state to determine where to send the user after a successful login. If there is no app state, we will send them to the redirect URL or dashboard later
  //Used in CCP/SSP/passwordless flows
  gatherAppState() {
    this.auth.appState$.subscribe(
      appState => {
        if (appState && appState?.target) {
          this.appStateTarget = appState.target;
          this.router.navigateByUrl(appState.target);
        }
      }
    );
  }

  determineLoginState() {
    this.auth.idTokenClaims$.pipe(take(1)).subscribe(
      (response: IdToken | null | undefined) => {
        if (!response) {
          this.initiatePasswordlessAuth();
        }
        if (response != null && response.__raw && response != undefined) {
          sessionStorage.setItem('data', JSON.stringify(response));
          sessionStorage.setItem('customized-claims-landing', JSON.stringify({ email: response.email }));
          sessionStorage.setItem('verifyData', JSON.stringify({ ...response, emailAddress: response.email }));
          this.email = response.email;
          if(this.flowType === 'pirateship' && !sessionStorage.getItem('pirateShipEncryptedParam') && !this.email.endsWith('@ups.com')) {
            this.router.navigateByUrl('/unauthorized');
          }
          let request: any = {
            id_token: response.__raw,
            workflow: this.channelPartner
          }
          // For pirate ship encrypted flow, include encrypted data for email verification
          if (this.flowType === 'pirateship' && sessionStorage.getItem('pirateShipEncryptedParam')) {
            request.encryptedData = sessionStorage.getItem('pirateShipEncryptedParam');
          }
          this.loginService.passwordLess(request).subscribe(
            data => {
              if (!data) {
                this.systemError = true;
                this.loading = false;
                return;
              }

              if (this.flowType === 'pirateship' && sessionStorage.getItem('pirateShipEncryptedParam')) {
                this.psEncryptedLandingSpinner = false;
                this.decryptPirateShipTrackingNumber();
              }
            }, error => {
              if (error.status == 400) {
                //There is likely a difference between the encrypted email address and the user's token. Redirect them to Auth0.
                this.initiatePasswordlessAuth();
              } else {
                this.psEncryptedLandingSpinner = false;
                this.loading = false;
                this.systemError = true;
              }
            }
          );
        }
      });
  }

  //Send verification code that user entered to api
  checkVerificationCode() {
    const data = JSON.parse(sessionStorage.getItem('data'));
    const trimmedTrackingNumber = this.customerForm.controls.trackingNumber.value.trim();
    this.gatherChannelPartnerShipmentDetails(trimmedTrackingNumber, this.channelPartner, data);

    // Set the groupCode to the email address for the pirateship flow type
    if (this.flowType === 'pirateship') {
      // Set up common claims interface with groupCode
      this.dataService.setCommonClaimsInterface({
        ...emptyClaim,
        groupCode: data?.email
      });
    }
  }

  // cancel() {
  //   this.loading2 = false;
  //   this.verifyCodeError = false;
  //   this.confirmFormGroup.reset();
  //   this.currentStep = 0;
  // }

  getPolicyInfoAndNavigate() {
    if (this.trackingNumber.value.toLowerCase().startsWith('1z')) {
      this.dataService.gatherTrack3ShipmentDetails(this.trackingNumber.value);  //If it is a 1Z, also store the track 3 shipment details. GW needs originScan for 1Z shipments.
    }
    this.dataService.getChannelPartnerShipmentDetails().pipe(
      filter(this.isNonNull), //Guarantees we ignore null values
      take(1) //Guarantees we only receive the value once - so we don't call policy search twice in some cases behavior subject returns same value multiple times.
    ).subscribe(
      shipmentInfo => {
        if (shipmentInfo == null) {
          return;
        }

        //We no longer get insured value from this API due to security changes
        // if (Number(shipmentInfo?.insuredValue) < 1) {
        //   if (this.flowType === 'pirateship') {
        //     this.isInsuredValueZero = false;
        //   } else {
        //     this.isInsuredValueZero = true;
        //     this.currentStep = 0;
        //     return;
        //   }
        // }

        //If no address info - see if we can get from track 3
        if (!shipmentInfo.destinationCity || !shipmentInfo.originCity) {
          if (this.trackingNumber.value.toLowerCase().startsWith('1z')) {
            this.dataService.getTrack3ShipmentDetails().pipe(
              filter(this.isNonNull),
              take(1)
            ).subscribe(
              data => {
                let track3 = this.dataService._track3ShipmentDetails;
                if (track3 && !track3.noInfoReturned) {
                  let destinationAddress = track3.shipperConsigneeAddress.find(x => x.description.toLowerCase() == 'shipto address');
                  let originAddress = track3.shipperConsigneeAddress.find(x => x.description.toLocaleLowerCase() == 'shipper address');
                  let incompleteShipInfo = shipmentInfo;
                  let completeShipInfo = {
                    ...incompleteShipInfo,
                    destinationAddress: destinationAddress.addressLine,
                    destinationCity: destinationAddress.city,
                    destinationCountryCode: destinationAddress.countryCode,
                    destinationPostalCode: destinationAddress.postalCode,
                    destinationState: destinationAddress.stateProvinceCode,
                    originAddress: originAddress.addressLine,
                    originCity: originAddress.city,
                    originCountryCode: originAddress.countryCode,
                    originPostalCode: originAddress.postalCode,
                    originState: originAddress.stateProvinceCode
                  }
                  this.dataService.setChannelPartnerShipmentDetails(completeShipInfo);
                  this.checkIfPolicyMatch(completeShipInfo);
                } else {
                  // incomplete address info and we could not get from track 3. continue anyway because security changes probably removed lots of info
                  // this.currentStep = 0;
                  // this.shipmentInformationError = true;
                  // return;
                  this.checkIfPolicyMatch(shipmentInfo);
                }
              }
            );
          } else {
            //NON-1Z TN had no address info. Stop.
            this.currentStep = 0;
            this.shipmentInformationError = true;
          }
        } else if (shipmentInfo) {
          //We got all the info from CBP - no additional logic needed so just continue.
          this.checkIfPolicyMatch(shipmentInfo);
        }
      }
    );
  }

  checkIfPolicyMatch(shipmentInfo) {
    //sessionStorage.setItem('channelPartner', JSON.stringify(shipmentDetails));
    let request = {
      partnerName: this.channelPartner,  //Hardcoded to unishippers for now that is all we support
      policyNumber: shipmentInfo.policyNumber
    };

    this.claimService.checkCCPPolicy(request).subscribe(
      response => {
        if (response.data && response.data.message && response.data.message == 'Match') {
          //Continue
          this.policyMatchError = false;
          this.userService.getPolicySearch(shipmentInfo.policyNumber).subscribe(
            policyData => {
              if (policyData?.message?.toLowerCase() != 'succeeded') {
                this.currentStep = 0;
                this.systemError = true;
                return;
              }
              else if (((Number(shipmentInfo?.insuredValue) <= 100) || (Number(shipmentInfo?.insuredValue) == 0) || (Number(shipmentInfo?.insuredValue) == null)) && (this.channelPartner == 'unishippers') && policyData?.data.policyType.key == 'Flex') {
                this.currentStep = 0;
                this.upsHealthCareDeclaredAmountError = true;
                return;
              }
              //Continue
              sessionStorage.setItem('policyDetails', JSON.stringify(policyData.data));
              this.userService.updateUserInfo({ policyNumber: shipmentInfo.policyNumber }); //Stored this so that user could continue to claim detail page
              //redirect for ccp to file a claim
              if (this.flowType === 'pirateship') {
                const channelPartner = JSON.parse(sessionStorage.getItem('channelPartner'));
                const policyDetails = JSON.parse(sessionStorage.getItem('policyDetails'));


                const shipDate = new Date(channelPartner.shipDate);
                const effectiveDate = new Date(policyDetails.effectiveDate);
                const expirationDate = new Date(policyDetails.expirationDate);

                if (shipDate > effectiveDate && shipDate < expirationDate) {
                  this.shipDateError = false;
                  this.createPirateShipClaimAndNavigate(shipmentInfo);
                } else {
                  this.shipDateError = true;
                  this.currentStep = 0;
                  this.loading = false;
                }
              } else {
                this.dynamicFlowService.redirectToFileClaimCcp();
              }
            }, error => {
              //System error
              this.currentStep = 0;
              this.systemError = true;
            }
          );
        } else {
          //Policy match error
          this.currentStep = 0;
          this.policyMatchError = true;
        }
      }, error => {
        //System error
        this.currentStep = 0;
        this.systemError = true;
      }
    );
  }
  createPirateShipClaimAndNavigate(shipmentInfo: any) {
  this.loading = true;
  this.systemError = false;
  sessionStorage.removeItem('documentUploadParams');
  sessionStorage.removeItem('documentUploadedFiles');
 
  this.dataService.populateClaimInterfaceUsingChannelPartnerInfo(shipmentInfo);
 
  const policyDetails = JSON.parse(sessionStorage.getItem('policyDetails') || '{}');
  if (policyDetails && policyDetails.policyNumber) {
  this.dataService._commonClaimsInterface.policy = {
    policyNumber: policyDetails.policyNumber,
    effectiveDate: policyDetails.effectiveDate ? new Date(policyDetails.effectiveDate).toISOString() : new Date().toISOString(),
    expirationDate: policyDetails.expirationDate ? new Date(policyDetails.expirationDate).toISOString() : new Date().toISOString(),
    cancellationDate: policyDetails.cancellationDate ? new Date(policyDetails.cancellationDate).toISOString() : null,
    insured: policyDetails.insured || '',
    policyType: policyDetails.policyType?.key || policyDetails.policyType || 'Flex'
  };
}
 
  this.dataService.formatPayloadBeforeAnyAPIRequest();
 
  const authData = JSON.parse(sessionStorage.getItem('data') || '{}');
  const email = sessionStorage.getItem('emailAddress');
  if (authData && authData.email) {
    this.dataService._commonClaimsInterface.groupCode = authData.email;
  } else if (email) {
    this.dataService._commonClaimsInterface.groupCode = email;
  }
 
  this.claimService.createClaim(this.dataService._commonClaimsInterface).subscribe(
    response => {
      this.loading = false;
      if (response && response.claimNumber) {
        sessionStorage.setItem('draftClaimNumber', response.claimNumber);
        sessionStorage.setItem('draftPublicId', response.publicId || '');
        sessionStorage.setItem('documentUploadParams', JSON.stringify({
          isCanadaUser: this.userService.isCanadaUser(),
          isCargo: false,
          claimNumber: response.claimNumber
        }));
        this.dynamicFlowService.redirectToFileClaimCcp();
      } else {
        this.currentStep = 0;
        this.systemError = true;
      }
    },
    error => {
      this.loading = false;
      this.currentStep = 0;
      this.systemError = true;
    }
  );
}
  private openPirateShipClaimsForAuthenticatedEmail() {
    const authData = JSON.parse(sessionStorage.getItem('data') || '{}');
    const email = this.email || authData?.email || authData?.emailAddress;

    if (!email) {
      this.systemError = true;
      return;
    }

    this.loading = true;
    this.systemError = false;
    sessionStorage.setItem('guestUserEmail', email);

    this.sspService.getPartnerPolicy({ partnerName: this.channelPartner }).subscribe(
      response => {
        const policyNumber = response?.data?.policyNumbers?.[0];
        if (!policyNumber) {
          this.loading = false;
          this.systemError = true;
          return;
        }

        sessionStorage.setItem('policyNumber', policyNumber);
        this.userService.loginForGuestUserNEW('/claims', true);
      },
      error => {
        this.loading = false;
        this.systemError = true;
      }
    );
  }

  viewMyFiledClaims() {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.flowType === 'pirateship') {
      this.openPirateShipClaimsForAuthenticatedEmail();
      return;
    }

    this.router.navigate(['/claims']);
  }

  get isMobile() {
    return this.breakpointObserver.isMatched('(max-width: 767px)');
  }

  get trackingNumber() { return this.customerForm.controls.trackingNumber }
  // get email() { return this.customerForm.controls.email }

  //Determines whether value is null/undefined or not
  //https://stackoverflow.com/questions/43043517/filter-all-null-values-from-an-observablet/60135405#60135405
  isNonNull<T>(value: T): value is NonNullable<T> {
    return value != null;
  }

  gatherChannelPartnerShipmentDetails(trackingNumber, channelPartner, tokenData) {
    if (this.channelPartner == 'inxpress') { // additional business logic needed for inxpress users when we call getShipmentDetails
      this.dataService.gatherChannelPartnerShipmentDetails(trackingNumber, this.channelPartner).subscribe(
        response => {
          if (response?.error == 'liability is false') { // if liability is false, do not allow them to continue
            this.loading = false;
            this.liabilityError = true;
          } else if (response?.error == 'inxpress system error') { // if getShipmentDetails fails, for inxpress users, do not allow them to continue
            this.loading = false;
            this.systemError = true;
          } else if (response?.error == 'no insured value') {
            this.loading = false;
            this.isInsuredValueZero = true;
          } else if (response?.error === 'tracking number not found') {
            this.loading = false;
            this.trackingNumberNotFound = true;
          } else if (response?.message === 'succeeded') {
            this.trackingNumberNotFound = false;
            this.systemError = false;
            this.determineWhereToSendUserAfterGatheringShipmentDetails(tokenData);
          } else {
            this.loading = false;
            this.systemError = true;
            this.trackingNumberNotFound = false;
          }
        }, error => {
          this.invalidTrackingNumber = false;
          if (error?.status == 400) {
            this.loading = false;
            this.invalidTrackingNumber = true;
          } else {
            this.loading = false;
            this.systemError = true;
          }
          this.currentStep = 0;
        }
      );
    } else if (this.flowType == 'pirateship') {
      this.dataService.gatherChannelPartnerShipmentDetails(trackingNumber, this.channelPartner).subscribe(
        response => {
          if (response?.message === 'succeeded') {
            this.trackingNumberNotFound = false;
            this.systemError = false;
            this.determineWhereToSendUserAfterGatheringShipmentDetails(tokenData);
          } else if (response?.error === 'tracking number not found') {
            this.loading = false;
            this.trackingNumberNotFound = true;
          } else {
            this.loading = false;
            this.systemError = true;
            this.trackingNumberNotFound = false;
          }
        }, error => {
          this.invalidTrackingNumber = false;
          if (error?.status == 400) {
            this.loading = false;
            this.invalidTrackingNumber = true;
          } else {
            this.loading = false;
            this.systemError = true;
          }
          this.currentStep = 0;
        }
      );
    } else { // for non-inxpress users
      // Gathering here because I want to make sure CP data loads first before everything else. In Ideal scenario it loads after user gets an access token
      this.dataService.gatherChannelPartnerShipmentDetails(trackingNumber, this.channelPartner).subscribe(
        response => {
          if (response?.message === 'succeeded') {
            this.trackingNumberNotFound = false;
            this.systemError = false;
            this.determineWhereToSendUserAfterGatheringShipmentDetails(tokenData);
          } else if (response?.error === 'tracking number not found') {
            this.loading = false;
            this.trackingNumberNotFound = true;
          } else {
            this.loading = false;
            this.systemError = true;
            this.trackingNumberNotFound = false;
          }
        }, error => {
          this.invalidTrackingNumber = false;
          if (error?.status == 400) {
            this.loading = false;
            this.invalidTrackingNumber = true;
          }
          else {
            this.loading = false;
            this.systemError = true;
          }
          this.currentStep = 0;
        }
      );
    }
  }

  determineWhereToSendUserAfterGatheringShipmentDetails(tokenData) {
    // Clear the decrypted data from session now that we've used it
    sessionStorage.removeItem('decryptedEmail');
    sessionStorage.removeItem('decryptedTrackingNumber');
    this.getPolicyInfoAndNavigate();
    this.userService.updateUserInfo({ trackingNumber: this.trackingNumber.value });
    this.loading2 = false;
  }


}
